import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButton from "@/components/base/ShareButton";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import useSeo from "@/hooks/useSeo";
import { formatDate, getActorById, getNewsByIds, getWorksByIds } from "@/lib/content";

export default function ActorDetail() {
  const { id = "" } = useParams();
  const actor = getActorById(id);

  useSeo({
    title: actor ? `${actor.name} - 演员资料 | 筱田优官网` : "演员资料 | 筱田优官网",
    description: actor ? actor.bio : "筱田优影视主创人员资料。",
    keywords: actor ? `${actor.name},演员资料,筱田优` : undefined,
  });

  if (!actor) {
    return (
      <div className="w-full pt-24 pb-16 px-4 md:px-8">
        <EmptyState
          icon="ri-user-line"
          title="暂无详情内容"
          description="该人物资料可能已被移除或链接有误，建议返回演员列表查看。"
          actionTo="/actors"
          actionLabel="返回演员列表"
        />
      </div>
    );
  }

  const works = getWorksByIds(actor.workIds);
  const relatedNews = getNewsByIds(actor.relatedNewsIds);

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: "演员", to: "/actors" }, { label: actor.name }]} />
      </div>

      <section className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-1">
            <div className="w-full max-w-xs mx-auto lg:mx-0 lg:max-w-none aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
              <img
                src={actor.photo}
                alt={`${actor.name}照片`}
                title={`${actor.name} - 演员资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
              <i className="ri-user-star-line" />
              {actor.role}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-4">
              {actor.name}
            </h1>
            <p className="text-base text-foreground-600 mt-4 leading-relaxed">{actor.bio}</p>

            <div className="mt-6 flex flex-col gap-4 text-base text-foreground-700 leading-loose">
              {actor.intro.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <ShareButton title={actor.name} />
              <Link
                to="/actors"
                className="px-4 h-10 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-arrow-left-line" />
                返回演员列表
              </Link>
            </div>
          </div>
        </div>
      </section>

      {works.length > 0 && (
        <section className="w-full px-4 md:px-8 mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-primary-500" />
            关联作品
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {works.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>
      )}

      {relatedNews.length > 0 && (
        <section className="w-full px-4 md:px-8 mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            相关新闻
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {relatedNews.map((news) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                className="group flex flex-col rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors bg-background-50"
              >
                <div className="w-full aspect-[16/10] overflow-hidden bg-background-200">
                  <img
                    src={news.cover}
                    alt={`${news.title}配图`}
                    title={`${news.title} - 筱田优影视资讯`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {news.title}
                  </h3>
                  <span className="text-xs text-foreground-400 mt-2 inline-block">{formatDate(news.date)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}